// import { CountryISO } from 'ngx-intl-tel-input';

export type AccessType = 'default' | 'free' | 'premium' | 'pass_only' | 'premium_channel';

export type AccessTypeIcon = 'default' | 'free' | 'premium' | 'nft_pass';

export type CardTypes =
  | 'video'
  | 'playlist'
  | 'channel'
  | 'channel_round'
  | 'video_watch'
  | 'video_watch_later'
  | 'video_watch_later_horizontal'
  | 'community_user'
  | 'community_video';

export interface Video {
  id: number;
  name: string;
  duration: number;
  owner: Channel;
  cover_url: string;
  thumbnail_url: string;
  access_type: AccessType;
  access_type_icon: AccessTypeIcon;
  video_token: string;
  description: string;
  tags: Tag[];
  all_tags: Tag[];
  reaction: number;
  reaction_enums: number[];
  is_watch_later: boolean;
  watching_progress: number;
  have_access: boolean;
  referral_link: string;
  video_access_options: {
    have_access: boolean;
    offer_type: BannerType;
  };
  offer: SubscriptionOffer[];
  year: string;
  age_ratings_id: string[];
  name_original_lang: string;
  name_original_lang_obj: Tag;
  trailer: Partial<Trailer>;
  video_types: Tag[];
  genres: Tag[];
  sub_genres: Tag[];
  moods: Tag[];
  countries_of_origin: CountryShort[];
  competition_participated: CompetitionShort[];
  competition_participated_id: number[];
  custom_passes_id: number[];
  awards_and_festivals: string[];
  filmed_by: string;
  premium_channel_id: number;
  mux_id?: string;
  trailer_url?: string;
  highlights: string;
  filmed_by_creators: CreatorItemModel[];
  promotion_channels: CreatorItemModel[];
}

export interface ShortVideoLiked {
  id: number;
  name: string;
  cover_url: string;
  duration: number;
  watching_progress: number;
  likes_count: number;
  liked_users: UserShort[];
}

export interface PromoVideo {
  id: number;
  name: string;
  description: string;
  thumbnail_url: string;
  cover_url: string;
  all_tags: Tag[];
  tags: Tag[];
  trailer_id: number;
  external_link: string;
}

export interface Playlist {
  id: number;
  name: string;
  cover_url: string;
  cover_original_url: string;
  access_type_icon: string;
  total_duration: number;
  channels: Channel[];
  owner: Channel;
  videos: Video[];
  reaction: number;
  is_watch_later: boolean;
  description: string;
  referral_link: string;
  count_videos: number;
  reaction_enums: number[];
  trailer: Partial<Trailer>;
  trailer_url?: string;
}

export type ChannelTypes = 'channel' | 'filmmaker';

export interface ChannelShort {
  trailer_url?: string;
  cover_url: string;
  created_at: Date;
  description: string;
  followers_count: number;
  id: number;
  is_followed: boolean;
  logo_url: string;
  name: string;
  playlists_count: number;
  type: ChannelTypes;
  users: Friend[];
  videos_count: number;
  web_cover_url: string;
  website: string;
}

export interface Channel {
  id: number;
  name: string;
  logo_url: string;
  cover_url: string;
  web_cover_url: string;
  is_followed: boolean;
  followers_count: number;
  site: SiteLink;
  facebook: SiteLink;
  youtube: SiteLink;
  instagram: SiteLink;
  twitter: SiteLink;
  tiktok: SiteLink;
  snapchat: SiteLink;
  reddit: SiteLink;
  type: ChannelTypes;
  referral_link: string;
  description: string;
  top_genres: TopItem[];
  creators: ChannelShort[];
  have_access: boolean;
  offer: SubscriptionOffer[];
  access_type: AccessType;
  followed_friends: Friend[];
  followed_friends_count: number;
  creators_count: number;
  videos_count: number;
  trailer_url?: string;
}

export interface Genre {
  id: string;
  name: string;
  icon_url: string;
  sub_genres: [];
  videos_count: number;
  active_cover_url: string;
  inactive_cover_url: string;
  followed_friends: Friend[];
  selected: boolean;
}

export interface Friend {
  id: number;
  name: string;
  avatar_url: string;
  cover_url: string;
}

export type TagType =
  | 'tags'
  | 'competition_tags'
  | 'genres'
  | 'moods'
  | 'sub_genres'
  | 'festivals_won'
  | 'keywords'
  | 'video_types'
  | 'countries_of_origin'
  | 'access_type'
  | 'filter_preset'
  | 'search'
  | 'filmed_by'
  | 'custom';

export enum TagTypeNames {
  tags = 'Tag',
  competition_tags = 'Tag',
  genres = 'Genre',
  moods = 'Mood',
  sub_genres = 'Sub Genre',
  festivals_won = 'Festival Won',
  keywords = 'Keyword',
  video_types = 'Video Type',
  countries_of_origin = 'Country Of Origin',
  access_type = 'Access Type',
  filter_preset = 'Filter Preset',
  search = 'Search',
  filmed_by = 'Film by',
  custom = '',
}

export interface Tag {
  id: string;
  name: string;
  type?: TagType;
}

export interface CountryShort {
  iso: string;
  name: string;
}

export interface CreatorItemModel {
  id: number;
  name: string;
  logo_url: string;
}

export interface SiteLink {
  link: string;
  icon?: string;
}

export interface SitelinkTyped extends SiteLink {
  type: SiteLinkTypes;
}

export enum SiteLinkTypes {
  SITE = 'site',
  FACEBOOK = 'facebook',
  TWITTER = 'twitter',
  INSTAGRAM = 'instagram',
  INFO = 'info',
  YOUTUBE = 'youtube',
  TIKTOK = 'tiktok',
  SNAPCHAT = 'snapchat',
  REDDIT = 'reddit',
}

export interface TokenResponse {
  token: string;
  user?: Partial<User>;
}

export interface DecodedToken {
  userId: string;
  referralCode: string;
}

export interface UserShort {
  avatar_url: string;
  cover_url: string;
  is_follower: boolean;
  is_following: boolean;
  is_on_argo: boolean;
  user_id: number;
  name: string;
}

export interface CommunityUserModel {
  user_id: number;
  username: string;
  display_name: string;
  is_influencer: boolean;
  avatar_url: string;
  cover_url: string;
  name: string;
  is_on_argo: boolean;
  is_follower: boolean;
  is_following: boolean;
  about: string;
  facebook: SiteLink;
  instagram: SiteLink;
  twitter: SiteLink;
  tiktok: SiteLink;
  snapchat: SiteLink;
}

export type User = {
  username: string;
  email: string;
  types: Type[];
  phone: string;
  description: string;
  passport_uuid: string;
  avatar_url: string;
  cover_url: string;
  display_name: string;
  social_networks: Record<any, unknown>;
  email_change_to: string;
  is_phone_verified: boolean | null;
};

export interface Type {
  id: string;
  name: string;
}

export interface ResponseList<T> {
  links?: {
    next: null | string;
    previous: null | string;
    next_cursor_id: string | null;
    previous_cursor_id: string | null;
  };
  list: T[];
  pages_total: number;
  total_results: number;
}

export interface ResponsePromocode {
  success: boolean;
  decline_reason: string;
  type: string;
  free_period: string;
}

export interface ClientDevice {
  id?: number;
  device_registration_id: string;
  device_type: 'web';
}

export interface SearchPreset {
  id: string;
  title: string;
}

export interface SelectedSearchModel extends SearchPreset {
  type: TagType;
}

export enum BannerType {
  GUEST_BANNER = 'GUEST_BANNER',
  PREMIUM_BANNER = 'PREMIUM_BANNER',
  X_DAYS_TRIAL_BANNER = 'X_DAYS_TRIAL_BANNER',
  LIMITED_X_TRIAL = 'LIMITED_X_TRIAL',
  DEFAULT_BANNER = 'DEFAULT_BANNER',
}

export interface Subscription {
  id: number;
  title: string;
  description: string;
  object_id: number;
  object_trait: SubscriptionTraitEnum;
  object_name: string;
  start_dt: string;
  end_dt: string;
  duration_days: number;
  status: SubscriptionStatusEnum;
  price: string;
  invoice: number;
  is_manual_canceled: boolean;
  is_periodic: boolean;
  user: number;
  payments_invoice: number;
  from_offer: Offer;
}

export interface BuyOfferParams {
  country?: string;
  provider_code: 'stripe';
  user_uuid?: string;
  object_id?: number;
  film_id?: number;
  playlist_id?: number;
  competition_id?: number;
}

export enum SubscriptionTraitEnum {
  ALL = 'all',
  VIDEO = 'video',
  PLAYLIST = 'playlist',
  CHANNEL = 'channel',
}

export enum SubscriptionStatusEnum {
  DRAFT = 'draft',
  PAID = 'paid',
  TRIAL = 'trial',
  CANCELED = 'canceled',
}

export interface SubscriptionShort {
  description: string;
  duration_days: number;
  id: number;
  is_active: boolean;
  is_periodic: boolean;
  old_price: string;
  price: string;
  title: string;
  trial_days_for_new_user: number;
}

export interface SubscriptionProvider {
  id: number;
  provider: 'apple_inapp' | 'stripe';
  product_key: string;
  external_prices: [];
}

export interface SubscriptionOffer {
  active_subscription: Date;
  active_subscription_id: number;
  active_trial: Date;
  active_object_id: { string: Date };
  cover_url: string;
  description: string;
  discount: number;
  display_price: string;
  duration_days: number;
  features: string[];
  external_products: SubscriptionProvider[];
  id: number;
  is_manual_canceled: boolean;
  is_periodic: false;
  object_id: number;
  object_trait: SubscriptionTraitEnum;
  old_price: string;
  price: string;
  skin: OfferSkinEnum;
  title: string;
  type: 'argo_pass';
  trial_days_for_new_user: number;
}

export interface Offer {
  cover_url: string;
  description: string;
  discount: string;
  display_price: string;
  duration_days: number;
  external_products: SubscriptionProvider[];
  features: string[];
  id: number;
  is_periodic: boolean;
  object_id: null;
  object_trait: SubscriptionTraitEnum;
  offer_type: OfferTypeEnum;
  old_price: string;
  price: string;
  skin: OfferSkinEnum;
  title: string;
  trial_days_for_new_user: number;
}

export enum OfferTypeEnum {
  AMBASSADOR = 'ambassador',
  LIMITED = 'limited',
  DEFAULT = 'default',
}

export enum OfferSkinEnum {
  DEFAULT = 'default',
  EMPHASE = 'emphase',
  DISCOUNT = 'discount',
}

export interface Trailer {
  id: number;
  name: string;
  trailer_file: string;
}

export type DeepLinkName =
  | 'film_link'
  | 'playlist_link'
  | 'creator_link'
  | 'user_link'
  | 'register_link'
  | 'trailer_link'
  | 'competition_link';

export type DeepLinkParam =
  | 'film_id'
  | 'playlist_id'
  | 'video_id'
  | 'creator_id'
  | 'share_user_id'
  | 'user_code'
  | 'token'
  | 'trailer_id'
  | 'competition_id';

export enum DeepLinkNameAndParam {
  'film_link' = 'film_id',
  'playlist_link' = 'playlist_id',
  'creator_link' = 'video_id',
  'user_link' = 'creator_id',
  'register_link' = 'user_code',
  'trailer_link' = 'token',
}

export type DeepLinkParams = Partial<Record<DeepLinkParam, number | string>>;

export interface DeepLinkRequest {
  name: DeepLinkName;
  params: DeepLinkParams;
}

export interface DeepLinkResponse {
  dynamic_link: string;
}

export interface TopItem {
  id: string;
  name: string;
  count: number;
}

export interface Competition {
  id: number;
  title: string;
  description: string;
  start_requests_dt: Date;
  end_requests_dt: Date;
  start_dt: Date;
  end_dt: Date;
  is_allow_requests: boolean;
  is_started: boolean;
  is_ended: boolean;
  logo_url: string;
  logo_svg_url: string;
  cover_url: string;
  web_background_url: string;
  feed_background_url: string;
  pass_cover_url: string;
  icon_url: string;
  top_genres: TopItem[];
  top_tags: TopItem[];
  last_winners_channel: CompetitionShort[];
  custom_passes_id: number[];
  have_access: boolean;
  competition_link: string;
  redirect: {
    type: 'channel' | 'competition';
    id: number;
  };
}

export interface NftCollection {
  id: number;
  name: string;
  description: string;
  image_url: string;
  opensea_link: string;
}

export interface CompetitionShort {
  id: number;
  title: string;
}

export interface UserWallet {
  address: string;
}

export interface ArgoPoint {
  id: number;
  is_single: boolean;
  name: string;
  description: string;
  description_full: string;
  value: number;
  icon_web_url: string;
  icon_small_url: string;
  icon_large_url: string;
  link: string;
  link_description: string;
}

export interface ArgoPointsList {
  points_total: number;
  user_actions: ArgoPoint[];
  filmmaker_actions: ArgoPoint[];
  single_actions: ArgoPoint[];
}

export interface StyleObject {
  backgroundImage?: string;
}

export interface CommunityUser {
  channel: ChannelShort;
  creator: ChannelShort;
  influencer: UserShort;
  user: UserShort;
}

export type IsFollowing = {
  is_following: boolean;
};

export const CHANNEL_DEFAULT_AVATAR = 'assets/img/default-user-pic.png';
export const COMMUNITY_DEFAULT_AVATAR = 'assets/img/community/default_userpic.jpg';

export type RoutePathParts = any[];
